import GoBackMenu from '@/components/GoBackNavbar';
import Loader from '@/components/Loader';
import WorkShopMeeting from '@/components/WorkshopMeeting';
import WorkshopTips from '@/components/WorkshopTips';
import WorkshopType from '@/components/WorkshopType';
import Constants from '@/Constants';
import { useCourse } from '@/hooks/useCourse';
import { useProgramClass } from '@/hooks/useProgramClass';
import { ProgramClass } from '@/models/ProgramClass';
import { Workshop, WorkshopTip } from '@/models/Workshop';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { getNewDate } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { updateProgramClassRequest } from '@/store/programClass/actions';
import { LocaleData } from '@/translations/LocaleData';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import { workshopFormInitialState, workshopSchema } from '../WorkshopClassForm/formConfig';
import './style.scss';

const ProgramWorkshopForm: React.FC = () => {
    const { courseId: courseIdString, classId: classIdString } = useParams<{ courseId: string; classId: string }>();

    const courseId = Number(courseIdString);
    const classId = Number(classIdString);

    const { course } = useCourse({ id: courseId });

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const [formState, setFormState] = useState({ ...workshopFormInitialState });
    const [formStateBkp, setFormStateBkp] = useState({ ...workshopFormInitialState });
    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    const { isOnline, address, host, meetings, tips } = formState;

    const { programClass, isLoading, mutate, deleteProgramClassKey } = useProgramClass({
        programClassId: classId,
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const dispatch = useDispatch();

    const history = useHistory();

    const workshop: Workshop | undefined = programClass?.workshops?.find((workshop: Workshop) => workshop.course.id === course?.id);

    const startDateStorage = localStorage.getItem(Constants.PROGRAM_CLASS_START_DATE_KEY);

    const startDate: Date | undefined = programClass?.startDate || programClass?.scheduledStartDate;

    const startDateForm: Date | undefined = !!startDateStorage ? getNewDate(new Date(startDateStorage)) : startDate;

    const handleOnValidateFormChange = (): boolean => {
        return !_.isEqual(formStateBkp, formState);
    };

    const handleOnChange = (
        fieldName: string,
        value: boolean | string | WorkshopMeeting[] | WorkshopTip[] | undefined,
        isBkp: boolean = false,
    ): void => {
        setFormState((prevState: any) => ({ ...prevState, [fieldName]: value }));

        if (isBkp) {
            if (_.includes(['meetings', 'tips'], fieldName)) {
                setFormStateBkp((prevState: any) => ({ ...prevState, [fieldName]: JSON.parse(JSON.stringify(value)) }));
            } else {
                setFormStateBkp((prevState: any) => ({ ...prevState, [fieldName]: value }));
            }
        }
    };

    const handleOnSave = () => {
        if (programClass) {
            let programClassToUpdate: ProgramClass = {
                ...programClass,
                workshops: !!workshop
                    ? [{ ...workshop, isOnline, address, course, programClass, host, meetings, tips }]
                    : [{ isOnline, address, course, programClass, host, meetings, tips }],
            } as ProgramClass;

            if (startDateForm) {
                const scheduledStartDateAsString = moment(startDateForm).format('YYYY-MM-DD');
                programClassToUpdate = { ...programClassToUpdate, scheduledStartDateAsString };
            }

            dispatch(updateProgramClassRequest(programClassToUpdate));
            mutate(programClassToUpdate, false);
            setTimeout(() => history.goBack(), 1000);
        }
    };

    const loadWorkshop = (): void => {
        if (workshop) {
            handleOnChange('isOnline', workshop.isOnline, true);
            handleOnChange('address', workshop.address, true);
            handleOnChange('host', workshop.host, true);
            handleOnChange('meetings', workshop.meetings, true);
            handleOnChange('tips', workshop.tips, true);
        }
    };

    const validateForm = (): void => {
        if (course?.id) {
            workshopSchema
                .validate(formState)
                .then(() => setIsFormValid(true))
                .catch(() => setIsFormValid(false));
        }
    };

    useEffect(() => {
        return () => {
            localStorage.removeItem(Constants.PROGRAM_CLASS_START_DATE_KEY);
            deleteProgramClassKey();
        };
    }, []);

    useEffect(loadWorkshop, [workshop]);

    useEffect(validateForm, [course, formState]);

    return (
        <div className="workshop-form-container">
            <GoBackMenu confirmable={handleOnValidateFormChange()} />

            <div className="class-form">
                <div className="class-header">
                    <div className="d-flex">
                        <img src={course?.mainImage} />
                        <div className="d-flex flex-column justify-content-center">
                            <span>{strings.SET_UP_WORKSHOP}</span>
                            <h4>{course?.name}</h4>
                        </div>
                    </div>
                </div>

                <div className="class-box-container">
                    {isLoading && <Loader />}
                    {!isLoading && (
                        <div className="class-form-container">
                            <section className="input-section">
                                <WorkshopType
                                    onChange={(isOnline, address) => {
                                        handleOnChange('isOnline', isOnline);
                                        handleOnChange('address', address);
                                    }}
                                />
                            </section>

                            <section className="input-section">
                                <WorkShopMeeting
                                    finishDateForm={programClass?.finishDate}
                                    onChange={(meetings: WorkshopMeeting[]) => handleOnChange('meetings', meetings)}
                                    startDateForm={startDateForm}
                                />
                            </section>

                            <section className="input-section">
                                <div className="container-workshop-instructor">
                                    <p>{strings.WORKSHOP_INFORMATION}</p>
                                    <span className="input-section-info">
                                        {strings.WORKSHOP_INSTRUCTOR}
                                        <span>*</span>
                                    </span>
                                    <input
                                        className="form-control"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('host', e.target.value)}
                                        placeholder={strings.WORKSHOP_INSTRUCTOR_PLACEHOLDER}
                                        required
                                        type="text"
                                        value={host}
                                    />
                                </div>
                            </section>

                            <section className="input-section">
                                <WorkshopTips onChange={(tips: WorkshopTip[]) => handleOnChange('tips', tips)} />
                            </section>

                            <div className="d-flex justify-content-end submit-buttons">
                                <button className="btn btn-link" onClick={() => history.goBack()} type="button">
                                    {strings.CANCEL}
                                </button>
                                <button
                                    className="btn btn-primary btn-sm"
                                    disabled={!isFormValid || _.isEqual(formStateBkp, formState)}
                                    onClick={handleOnSave}
                                    type="button"
                                >
                                    {strings.SAVE}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProgramWorkshopForm;
